import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { FormProps } from '../../helpers';
import ListToggle from '../../list-toggle';
import { UiOption } from '@/lib/helpers';
import { Box, Stack } from '@mui/material';
import useQueryHelper from '@/hooks/use-query-helper';
import { QueryKey } from '@/lib/query-client';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import {
  getMcpToolOptions,
  McpControls,
} from '@/lib/models/policy-model-v1/mcp-controls';
import { cloneDeep, difference } from 'lodash';
import { McpToolModel } from '@/lib/models/mcp-tool/mcp-tool.model';
import { useQuery } from 'react-query';
import { getAllMcpServers, getAllMcpTools } from '@/lib/services/mcp.service';
import McpToolButton from '@/components/mcp-tool-button';
import { McpServerModel } from '@/lib/models/mcp-server.model';
import useDrawer from '@/hooks/use-drawer.hook';

interface Props extends FormProps {
  value: McpControls;
  onChange: (value: McpControls) => void;
  orgPolicy?: PolicyModelV1;
  aclVariant?: 'allow' | 'block';
}

const useStyles = createUseStyles({
  mcpToolsList: {
    '& label.MuiFormControlLabel-root': {
      alignItems: 'flex-start',
      marginRight: 0,
    },
    '& span.MuiFormControlLabel-label': {
      display: 'block',
      width: '100%',
    },
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      flexGrow: 1,
    },
  },
});

const getToolDisabledTooltip = (
  isAllowList: boolean,
  orgAllowDisabled: boolean,
  orgBlockDisabled: boolean
): string => {
  if (isAllowList) {
    return orgAllowDisabled
      ? 'This tool is allowed by default policy and cannot be blocked.'
      : 'This tool is on the block list and cannot be allowed.';
  }

  return orgBlockDisabled
    ? 'This tool is blocked by default policy. To allow it, add it to the allow list.'
    : 'This tool is on the allow list and cannot be blocked.';
};

const McpToolsAclV1: FC<Props> = ({
  aclVariant = 'allow',
  value,
  onChange,
  orgPolicy,
  ...props
}) => {
  const styles = useStyles();
  const isAllowList = aclVariant === 'allow';

  const { openDrawer } = useDrawer();

  const handleView = useCallback(
    (event: MouseEvent, { id: defaultToolId, serverId }: McpToolModel) => {
      event.stopPropagation(); // prevent clicking on info from opening the button accordion
      event.preventDefault();
      // Pass tab as a prop so it gets set in the URL before the drawer mounts
      openDrawer('mcp-server', { id: serverId, defaultToolId, tab: 'tools' });
    },
    [openDrawer]
  );

  const query = useQuery([QueryKey.McpToolList], async () => getAllMcpTools());
  const { data = [] } = query;
  const { showLoader: toolsLoading } = useQueryHelper(query);

  const serverQuery = useQuery([QueryKey.McpServersList], async () => getAllMcpServers());
  const { data: mcpServers } = serverQuery;
  const { showLoader: serversLoading } = useQueryHelper(serverQuery);

  const serverMap: Map<string, McpServerModel> = useMemo(() => {
    const map: Map<string, McpServerModel> = new Map();
    mcpServers?.forEach((server) => {
      map.set(server.id, server);
    });
    return map;
  }, [mcpServers]);

  const {
    tools: { enabled, disabled },
  } = value;

  const onListToggleChange = useCallback(
    (option: UiOption[]) => {
      const updated = cloneDeep(value);
      const updateKey = isAllowList ? 'enabled' : 'disabled';
      const alternateKey = isAllowList ? 'disabled' : 'enabled';
      updated.tools[updateKey] = option.map(({ value }) => value);
      updated.tools[alternateKey] = difference(
        updated.tools[alternateKey],
        updated.tools[updateKey]
      );
      onChange(updated);
    },
    [value, onChange, isAllowList]
  );

  const toolOptions: UiOption[] = useMemo(() => {
    return getMcpToolOptions(
      data,
      aclVariant,
      new PolicyModelV1({ mcpControls: value }),
      orgPolicy
    ).map(({ value, meta }) => {
      const { mcpTool, disabledByAlternateList, enabledByOrgPolicy, disabledByOrgPolicy } =
        meta as {
          mcpTool: McpToolModel;
          disabledByAlternateList: boolean;
          enabledByOrgPolicy: boolean;
          disabledByOrgPolicy: boolean;
        };

      const server = serverMap.get(mcpTool.serverId);

      // you cannot remove a tool from the allow list, if it's allowed by org policy
      const orgAllowDisabled = isAllowList && enabledByOrgPolicy;
      // you cannot remove a tool from the block list, if it's blocked by org policy
      const orgBlockDisabled = !isAllowList && disabledByOrgPolicy;

      const disabled = disabledByAlternateList || orgAllowDisabled || orgBlockDisabled;

      const LabelEl = (
        <Tooltip
          title={getToolDisabledTooltip(isAllowList, orgAllowDisabled, orgBlockDisabled)}
          disabled={!disabled}
        >
          <Stack direction="row" alignItems="center" width="100%" gap={1}>
            {disabled && <Icon name="lock" size="small" color="grey" />}
            <McpToolButton mcpTool={mcpTool} mcpServer={server} onInfoClick={handleView} />
          </Stack>
        </Tooltip>
      );

      return {
        label: LabelEl,
        value,
        meta: { keyword: `${server?.title ?? ''} ${mcpTool.name} ${mcpTool.description}` },
        disabled,
      };
    });
  }, [data, handleView, orgPolicy, serverMap, value, aclVariant, isAllowList]);

  const isLoading = toolsLoading || serversLoading;

  const rightTitle = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Icon name={isAllowList ? 'check' : 'block'} size="small" />
      {isAllowList ? 'Allowed MCP Tools' : 'Blocked MCP Tools'}
    </Stack>
  );

  return (
    <Box className={styles.mcpToolsList}>
      <ListToggle
        {...props}
        value={isAllowList ? enabled : disabled}
        onChange={onListToggleChange}
        label="MCP Tools"
        leftTitle="Available MCP Tools"
        rightTitle={rightTitle}
        options={toolOptions}
        labelChecks={true}
        loadState={isLoading ? 'loading' : 'loaded'}
        rowHeight={70}
        searchPlaceholder="Search by tool or server name"
      />
    </Box>
  );
};

export default McpToolsAclV1;
